import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";

import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import BookAppointment from "@/pages/BookAppointment";
import BookSuccess from "@/pages/BookSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAppointments from "@/pages/admin/AdminAppointments";
import AdminServices from "@/pages/admin/AdminServices";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminPOS from "@/pages/admin/AdminPOS";
import AdminClients from "@/pages/admin/AdminClients";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminReports from "@/pages/admin/AdminReports";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import StaffDashboard from "@/pages/staff/StaffDashboard";
import StaffReports from "@/pages/staff/StaffReports";
import Terms from "@/pages/Terms";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CancelAppointment from "@/pages/CancelAppointment";

function ProtectedRoute({ children, requireAdmin = false, requireStaff = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (requireStaff && user.role !== "staff" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/cancel/:token" element={<CancelAppointment />} />

            {/* Tenant-scoped public booking
                Primary URL: /{slug}  (shorter, easier to remember)
                Legacy alias: /{slug}/book (kept for backwards compatibility) */}
            <Route path="/:slug" element={<BookAppointment />} />
            <Route path="/:slug/book" element={<BookAppointment />} />
            <Route path="/:slug/book/success" element={<BookSuccess />} />
            <Route path="/:slug/success" element={<BookSuccess />} />

            <Route
              path="/dashboard"
              element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>}
            />
            <Route
              path="/admin"
              element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}
            >
              <Route index element={<AdminDashboard />} />
              <Route path="calendar" element={<AdminCalendar />} />
              <Route path="appointments" element={<AdminAppointments />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="pos" element={<AdminPOS />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            <Route
              path="/staff/me"
              element={<ProtectedRoute requireStaff><StaffDashboard /></ProtectedRoute>}
            />
            <Route
              path="/staff/reports"
              element={<ProtectedRoute requireStaff><StaffReports /></ProtectedRoute>}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
      <p className="overline">404</p>
      <h1 className="text-4xl font-light">Página no encontrada</h1>
      <Link to="/" className="text-primary underline" data-testid="404-home-link">Volver al inicio</Link>
    </div>
  );
}

export default App;
